// AdminDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../utils/axios';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import "./Admindshbaord.css"
import Dashboardnavbar from '../../layout/Navbar/Navbar';
import { ToastContainer, toast } from "react-toastify";
import { Documentation } from '../../utils/ReusableSVGs';
// Color palette for project cards
const PROJECT_COLORS = [
    '#4E79A7', '#F28E2B', '#E15759', '#76B7B2',
    '#59A14F', '#EDC948', '#B07AA1', '#FF9DA7',
    '#9C755F', '#BAB0AC'
];

const AdminDashboard = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProject, setSelectedProject] = useState(null);

    const fetchProjectProgress = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/project_details/projecttasksprogress/null/`);
            setProjects(res.data);
        } catch (error) {
            console.error('Error fetching project progress:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjectProgress();
    }, []);

    if (loading) {
        return <div className="spinner-bgdatil"></div>;
    }

    if (selectedProject) {
        return <ProjectDetailView project={selectedProject} onBack={() => setSelectedProject(null)} />;
    }

    return (
        <>
            <Dashboardnavbar name={"Project Analysis Dashboard"} url="Project Analysis Dashboard" />
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <div className="dashboard-container">
                <div className="projects-grid">
                    {projects.map((project, index) => (
                        <ProjectCard
                            key={project.project_id}
                            project={project}
                            color={PROJECT_COLORS[index % PROJECT_COLORS.length]}
                            onClick={() => setSelectedProject(project)}
                        />
                    ))}
                </div>
            </div>
        </>
    );
};

const ProjectCard = ({ project, color, onClick }) => {
    return (
        <div className="project-card" onClick={onClick} style={{ borderTop: `4px solid ${color}` }}>
            <div className="project-card-header">
                <h3>{project.project_name}</h3>

            </div>
            <div className="single-progress-container">
                <div className="progress-label">
                    <span>Overall Progress</span>
                    <span>{Math.round(project.average_completion_percentage)}%</span>
                </div>
                <div className="progress-bar-container">
                    <div
                        className="progress-bar-fill"
                        style={{
                            width: `${project.average_completion_percentage}%`,
                            backgroundColor: color
                        }}
                    ></div>
                </div>
            </div>
            <div className="progress-stats">
                <ProgressItem
                    label="Tasks"
                    completed={project.tasks_summary.completed_tasks}
                    total={project.tasks_summary.total_tasks}
                    percent={project.tasks_summary.progress_percent}
                />
                <ProgressItem
                    label="MOMs"
                    completed={project.moms_summary.completed_moms}
                    total={project.moms_summary.total_moms}
                    percent={project.moms_summary.progress_percent}
                />
                <ProgressItem
                    label="Materials"
                    completed={project.materials_summary.completed_materials}
                    total={project.materials_summary.total_materials}
                    percent={project.materials_summary.progress_percent}
                />
                <ProgressItem
                    label="Activities"
                    completed={project.activities_summary.completed_activities}
                    total={project.activities_summary.total_activities}
                    percent={project.activities_summary.progress_percent}
                />
                <ProgressItem
                    label="ROBs"
                    completed={project.robs_summary.completed_robs}
                    total={project.robs_summary.total_robs}
                    percent={project.robs_summary.progress_percent}
                />
            </div>
        </div>
    );
};

const ProgressItem = ({ label, completed, total, percent }) => {
    const getProgressColor = (percentage) => {
        if (percentage >= 80) return '#4CAF50';
        if (percentage >= 50) return '#FFC107';
        return '#F44336';
    };

    return (
        <div className="progress-item">
            <div className="progress-circle">
                <CircularProgressbar
                    value={percent}
                    text={`${percent}%`}
                    styles={buildStyles({
                        pathColor: getProgressColor(percent),
                        textColor: '#333',
                    })}
                />
            </div>
            <div className="progress-info">
                <span className="progress-label">{label}</span>
                <span className="progress-count">{completed}/{total}</span>
            </div>
        </div>
    );
};

const ProjectDetailView = ({ project, onBack }) => {
    return (
        <div className="project-detail-container">
            <button className="back-button" onClick={onBack}>
                &larr; Back to Dashboard
            </button>

            <div className="project-detail-header">
                <h2>{project.project_name}</h2>

            </div>

            {/* Stats Summary */}
            <div className="stats-summary-row">
                <StatCard
                    title="Tasks"
                    completed={project.tasks_summary.completed_tasks}
                    total={project.tasks_summary.total_tasks}
                    percent={project.tasks_summary.progress_percent}
                    color="#4CAF50"
                />
                <StatCard
                    title="MOMs"
                    completed={project.moms_summary.completed_moms}
                    total={project.moms_summary.total_moms}
                    percent={project.moms_summary.progress_percent}
                    color="#2196F3"
                />
                <StatCard
                    title="Materials"
                    completed={project.materials_summary.completed_materials}
                    total={project.materials_summary.total_materials}
                    percent={project.materials_summary.progress_percent}
                    color="#FF9800"
                />
                <StatCard
                    title="Activities"
                    completed={project.activities_summary.completed_activities}
                    total={project.activities_summary.total_activities}
                    percent={project.activities_summary.progress_percent}
                    color="#9C27B0"
                />
                <StatCard
                    title="ROBs"
                    completed={project.robs_summary.completed_robs}
                    total={project.robs_summary.total_robs}
                    percent={project.robs_summary.progress_percent}
                    color="#607D8B"
                />
            </div>

            {/* Hierarchical View */}
            <div className="hierarchical-view">
                <h3 className="section-title">Project Structure</h3>

                {/* Activities Section */}
                <div className="activity-section">
                    <h4 className="subsection-title">Activities</h4>
                    {project.activities_summary.activities.length > 0 ? (
                        project.activities_summary.activities.map(activity => (
                            <ActivityItem
                                key={activity.id}
                                activity={activity}
                                tasks={project.tasks_summary.tasks.filter(t =>
                                    t.activities_summary?.activities?.some(a => a.id === activity.id)
                                )}
                            />
                        ))
                    ) : (
                        <div className="no-data">No activities found</div>
                    )}
                </div>

                {/* Standalone Tasks Section */}
                {project.tasks_summary.tasks.filter(t =>
                    !project.activities_summary.activities.some(a =>
                        a.tasks_summary?.tasks?.some(at => at.id === t.id)
                    )
                ).length > 0 && (
                        <div className="standalone-tasks-section">
                            <h4 className="subsection-title">Standalone Tasks</h4>
                            {project.tasks_summary.tasks
                                .filter(t => !project.activities_summary.activities.some(a =>
                                    a.tasks_summary?.tasks?.some(at => at.id === t.id))
                                )
                                .map(task => (
                                    <TaskItem
                                        key={task.id}
                                        task={task}
                                        level={0}
                                    />
                                ))
                            }
                        </div>
                    )}

                {project.moms_summary.moms.length > 0 && (
                    <div className="mom-tasks-section">
                        <h4 className="subsection-title">MOM Tasks</h4>
                        {project.moms_summary.moms.map(mom => (
                            <MOMItem
                                key={mom.id}
                                mom={mom}
                                tasks={project.tasks_summary.tasks.filter(t =>
                                    t.moms_summary?.moms?.some(m => m.id === mom.id)
                                )}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const MOMItem = ({ mom, tasks }) => {
    const [expanded, setExpanded] = useState(true);

    return (
        <div className="mom-item">
            <div className="mom-header" onClick={() => setExpanded(!expanded)}>
                <span className={`toggle-icon ${expanded ? 'expanded' : ''}`}>▸</span>
                <span className="mom-name">{mom.description}</span>
                <span className={`status-badge ${mom.status}`}>{mom.status}</span>
                <span className="mom-date">
                    Meeting Date: {mom.meeting_date}
                </span>
            </div>

            {expanded && (
                <div className="mom-content">
                    <div className="mom-details">
                        <div className="detail-row">
                            <span className="detail-label">Location:</span>
                            <span className="detail-value">{mom.location || '-'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Attendees:</span>
                            <span className="detail-value">{mom.attendees?.join(', ') || '-'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Discussion Points:</span>
                            <span className="detail-value">{mom.discussion_points || '-'}</span>
                        </div>

                    </div>

                    {/* Related Tasks */}
                    {tasks.length > 0 && (
                        <div className="related-tasks">
                            <h5 className="tasks-title">Action Items</h5>
                            {tasks.map(task => (
                                <TaskItem
                                    key={task.id}
                                    task={task}
                                    level={1}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
const StatCard = ({ title, completed, total, percent, color }) => {
    return (
        <div className="stat-card">
            <div className="stat-progress">
                <CircularProgressbar
                    value={percent}
                    text={`${percent}%`}
                    styles={buildStyles({
                        pathColor: color,
                        textColor: '#333',
                        textSize: '24px',
                    })}
                />
            </div>
            <div className="stat-info">
                <h4>{title}</h4>
                <p>{completed}/{total}</p>
            </div>
        </div>
    );
};
const ActivityItem = ({ activity, tasks }) => {
    const [expanded, setExpanded] = useState(true);

    return (
        <div className="activity-item">
            <div className="activity-header" onClick={() => setExpanded(!expanded)}>
                <span className={`toggle-icon ${expanded ? 'expanded' : ''}`}>▸</span>
                <span className="activity-name">{activity.name}</span>
                <span className={`status-badge ${activity.status}`}>{activity.status}</span>
                <span className="activity-dates">
                    {activity.start_date} to {activity.end_date}
                </span>
                <span className="activity-progress">
                    {activity.completed_till_end}/{activity.scope_quantity} {activity.unit}
                </span>
            </div>

            {expanded && (
                <div className="activity-content">
                    <div className="activity-details">
                        <div className="detail-row">
                            <span className="detail-label">Assigned To:</span>
                            <span className="detail-value">{activity.assigned_to?.join(', ') || '-'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Progress:</span>
                            <span className="detail-value">
                                {activity.achieved_this_month} of {activity.target_of_month} this month
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Remarks:</span>
                            <span className="detail-value">{activity.remarks || '-'}</span>
                        </div>
                    </div>

                    {/* Related Tasks */}
                    {tasks.length > 0 && (
                        <div className="related-tasks">
                            <h5 className="tasks-title">Related Tasks</h5>
                            {tasks.map(task => (
                                <TaskItem
                                    key={task.id}
                                    task={task}
                                    level={1}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const TaskItem = ({ task, level }) => {
    const [expanded, setExpanded] = useState(level === 0);

    return (
        <div className={`task-item level-${level}`}>
            <div className="task-header" onClick={() => setExpanded(!expanded)}>
                <span className={`toggle-icon ${expanded ? 'expanded' : ''}`}>
                    {task.sub_tasks?.length > 0 ? '▸' : '•'}
                </span>
                <span className="task-name">{task.task_name}</span>
                <span className={`status-badge ${task.status}`}>{task.status}</span>
                <span className="task-dates">
                    Deadline: {task.deadline_date}
                </span>
                <span className="task-priority">
                    Priority: {task.priority}
                </span>
            </div>

            {expanded && (
                <div className="task-content">
                    <div className="task-details">
                        <div className="detail-row">
                            <span className="detail-label">Assigned To:</span>
                            <span className="detail-value">{task.assigned_to?.join(', ') || '-'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Remarks:</span>
                            <span className="detail-value">{task.remarks || '-'}</span>
                        </div>
                    </div>

                    {/* Sub-tasks */}
                    {task.sub_tasks?.length > 0 && (
                        <div className="sub-tasks">
                            <h5 className="subtasks-title">Sub-tasks</h5>
                            {task.sub_tasks.map(subTask => (
                                <TaskItem
                                    key={subTask.id}
                                    task={subTask}
                                    level={level + 1}
                                />
                            ))}
                        </div>
                    )}

                    {/* Related Materials */}
                    {task.materials_summary?.materials?.length > 0 && (
                        <div className="related-materials">
                            <h5 className="materials-title">Required Materials</h5>
                            <div className="materials-grid">
                                {task.materials_summary.materials.map(material => (
                                    <MaterialItem
                                        key={material.id}
                                        material={material}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Related ROBs */}
                    {task.robs_summary?.robs?.length > 0 && (
                        <div className="related-robs">
                            <h5 className="robs-title">Related ROBs</h5>
                            {task.robs_summary.robs.map(rob => (
                                <ROBItem
                                    key={rob.id}
                                    rob={rob}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const MaterialItem = ({ material }) => {
    return (
        <div className="material-item">
            <div className="material-header">
                <span className="material-name">{material.item_description}</span>
                <span className={`status-badge ${material.status}`}>{material.status}</span>
            </div>
            <div className="material-details">
                <div className="detail-row">
                    <span className="detail-label">Quantity:</span>
                    <span className="detail-value">
                        {material.upto_date_received}/{material.project_scope_quantity} {material.unit}
                    </span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Balance:</span>
                    <span className="detail-value">{material.balance_to_be_procure} {material.unit}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Remarks:</span>
                    <span className="detail-value">{material.remarks || '-'}</span>
                </div>
            </div>
        </div>
    );
};

const ROBItem = ({ rob }) => {
    return (
        <div className="rob-item">
            <div className="rob-header">
                <span className="rob-activity">{rob.activity}</span>
                <span className={`status-badge ${rob.status}`}>{rob.status}</span>
            </div>
            <div className="rob-details">
                <div className="detail-row">
                    <span className="detail-label">Progress:</span>
                    <span className="detail-value">
                        Yesterday: {rob.status_yesterday}, Today: {rob.status_today}
                    </span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Target:</span>
                    <span className="detail-value">{rob.target_this_week}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Action:</span>
                    <span className="detail-value">{rob.action_to_be_taken || '-'}</span>
                </div>
                <div className="detail-row">
                    <span className="detail-label">Remarks:</span>
                    <span className="detail-value">{rob.remarks || '-'}</span>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;